import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import HeroCarouselCard from './heroCarouselCard';
import styles from './heroCarousel.module.scss';
import LeftArrow from './images/LeftArrow.svg';
import RightArrow from './images/RightArrow.svg';
import DotActive from './images/dot-active.svg';
import DotInactive from './images/dot-inactive.svg';

function HeroCarousel({ items = [], onCardClick, mailOfferContent }) {
  const [swiper, setSwiper] = useState(null);
  const [isEnd, setIsEnd] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [allCardsVisible, setAllCardsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLastCardVisible, setIsLastCardVisible] = useState(false);

  const checkIfLastCardVisible = (swiperInstance) => {
    if (!swiperInstance || !swiperInstance.slides.length) return false;
    try {
      const progress = swiperInstance.progress || 0;
      return progress >= 0.98;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!swiper) return undefined;

    const checkIfAllCardsVisible = () => {
      const { slides } = swiper;
      if (slides.length === 0) return;

      const swiperWidth = swiper.width;
      let totalSlidesWidth = 0;

      slides.forEach((slide) => {
        totalSlidesWidth += slide.offsetWidth;
      });

      const spaceBetween = swiper.params.spaceBetween || 0;
      totalSlidesWidth += spaceBetween * (slides.length - 1);

      setAllCardsVisible(totalSlidesWidth <= swiperWidth);
    };

    checkIfAllCardsVisible();
    setIsLastCardVisible(checkIfLastCardVisible(swiper));

    const handleResize = () => {
      checkIfAllCardsVisible();
      setIsLastCardVisible(checkIfLastCardVisible(swiper));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [swiper]);

  const swiperConfig = {
    modules: [Navigation],
    slidesPerView: 'auto',
    spaceBetween: 20,
    speed: 800,
    slidesOffsetAfter: 20,
    slidesOffsetBefore: 0,
    onSwiper: (swiperInstance) => {
      setSwiper(swiperInstance);
      setIsEnd(swiperInstance.isEnd);
      setActiveIndex(swiperInstance.activeIndex);
      setIsLastCardVisible(checkIfLastCardVisible(swiperInstance));
    },
    onSlideChange: (swiperInstance) => {
      setIsEnd(swiperInstance.isEnd);
      setActiveIndex(swiperInstance.activeIndex);
      setIsLastCardVisible(checkIfLastCardVisible(swiperInstance));
    },
    onProgress: (swiperInstance) => {
      setIsLastCardVisible(checkIfLastCardVisible(swiperInstance));
    },
  };

  if (!items || items.length === 0) {
    return null;
  }

  const handleNext = () => {
    if (swiper) {
      swiper.slideNext();
    }
  };

  const handlePrevious = () => {
    if (swiper) {
      swiper.slidePrev();
    }
  };

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !touchStart || !touchStartY) return;

    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    const deltaX = Math.abs(currentX - touchStart);
    const deltaY = Math.abs(currentY - touchStartY);

    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }
  };

  const canScroll = items.length > 1 && !allCardsVisible;
  const hasMoreCards = canScroll && !isEnd && !isLastCardVisible;
  const hasPreviousCards = canScroll && activeIndex > 0;

  const handleDotClick = (index) => {
    if (swiper) {
      swiper.slideTo(index);
    }
  };

  return (
    <div className={styles.heroCarouselSection}>
      <div
        className={styles.srOnly}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {items[activeIndex]?.headline
          ? `Slide ${activeIndex + 1} of ${items.length}: ${
              items[activeIndex].headline
            }`
          : ''}
      </div>
      <div
        className={styles.carouselContainer}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        aria-roledescription="carousel"
        aria-label="Loan purpose options"
      >
        <div className={styles.firstCardGlow} />

        <Swiper {...swiperConfig} className={styles.carouselWrapper}>
          {items.map((item, index) => (
            <SwiperSlide key={item.id || index}>
              <HeroCarouselCard
                item={item}
                onCardClick={onCardClick}
                mailOfferContent={mailOfferContent}
                isFirstCard={index === 0}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {hasPreviousCards && !isMobile && (
          <button
            type="button"
            onClick={handlePrevious}
            className={styles.arrowButtonLeft}
            aria-label="Show previous cards"
          >
            <img src={LeftArrow.src || LeftArrow} alt="" />
          </button>
        )}

        {hasMoreCards && !isMobile && (
          <button
            type="button"
            onClick={handleNext}
            className={styles.arrowButtonRight}
            aria-label="Show next cards"
          >
            <img src={RightArrow.src || RightArrow} alt="" />
          </button>
        )}
      </div>

      {isMobile && items.length > 1 && (
        <div className={styles.dotIndicators}>
          {items.map((item, index) => (
            <button
              key={item.id || index}
              type="button"
              onClick={() => handleDotClick(index)}
              className={styles.dotButton}
              aria-label={`Go to slide ${index + 1}`}
            >
              <img
                src={
                  activeIndex === index
                    ? DotActive.src || DotActive
                    : DotInactive.src || DotInactive
                }
                alt=""
                width="10"
                height="10"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default HeroCarousel;
